/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack, Typography } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import Input from 'components/forms/Input'
import SimpleTextArea from '../../../../components/forms/SimpleTextArea'
import ProgressCard from '../../../../components/card/ProgressCard'
import PopulationField from '../components/PopulationField'
import ManagementUnitSkillsPanel from './ManagementUnitSkillsPanel'
import { formatThousands } from '../../../utils/RPQSUtils'
import useActions from 'utils/customHook/useActions'
import { push } from '@lagunovsky/redux-react-router'
import { useParams } from 'react-router'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Checkbox from 'components/forms/Checkbox'
import { ALIGN, CardTable } from 'components/datatable/NewTable'
import { mainBlue } from 'utils/constants/ColorTheme'
import CityAction from 'referencial/components/city/actions/CityAction'
import RpqsAction from 'rpqs/actions/RpqsAction'
import ReferencialAction from 'referencial/action/ReferencialAction'
import ContactAction from 'referencial/components/contact/actions/ContactAction'
import { isNil, omit, uniqBy } from 'lodash'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY, PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY_NEW } from 'home/constants/RouteConstants'
import NumberField from 'components/forms/NumberField'
import DashboardIcon from '@mui/icons-material/Dashboard'
import HandshakeIcon from '@mui/icons-material/Handshake'
import ContactsIcon from '@mui/icons-material/Contacts'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import DialogContacts from '../components/DialogContacts'
import DialogCities from '../components/DialogCities'
import DialogConventions from '../components/DialogConventions'
import DialogStations from '../components/DialogStations'
import InstallationThunk from '../../../../installation/actions/InstallationThunk'
import DistributionUnitThunk from '../../../../distributionUnit/actions/DistributionUnitThunk'
import { STATION_NAME_ASSOCIATION, STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from '../../../../station/constants/StationConstants'
import { getImgInstallation, getSiteUrl } from '../../../../utils/mapUtils/SiteTypes'

const HEADERS_CONTACTS = ['name', 'phoneTel', 'email', 'socialReason']
const HEADERS_POPULATION = ['city', 'year', 'totalPopulationINSEE', 'temporaryResidents', 'adjustedTotalPopulation']
const HEADERS_CONVENTIONS = ['agreement', 'providerName', 'characteristics', 'contractualRelationship']
const HEADERS_STATIONS = ['nullValue', 'code', 'name']

const getLinkedStationCodes = (links, stationType) => (links ?? [])
    .filter(link => link.stationType === stationType)
    .map(link => link.stationCode)

// La sélection porte sur le code (stocké dans lien_stations), pas sur l'id
const getSelectableStations = (stations) => uniqBy(stations.filter(s => s.code), 'code').map(s => ({ id: s.code, code: s.code, name: s.name }))

const getInstallationIcon = installation => getImgInstallation(installation?.installationType).url
const getDistributionUnitIcon = () => getSiteUrl(STATION_NAME_ASSOCIATION.distributionUnit)

const getStationRows = (codes, stations, getIcon) => codes.map(code => {
    const station = stations.find(s => s.code === code)
    return {
        id: station?.id,
        nullValue: { value: <img src={getIcon(station)} style={{ maxHeight: '25px' }} /> },
        code: { value: code },
        name: { value: station?.name },
    }
})

const getStationLinks = (codes, stationType, managementUnitCode) => codes.map(stationCode => ({ managementUnitCode, stationType, stationCode }))

const SKILL_CONVENTION_KEYS = ['productionConvention', 'protectionConvention', 'treatmentConvention', 'transportConvention', 'storageConvention', 'distributionConvention']

// Les avenants arrivent à plat (link_conventionAmendments) : ils sont rattachés à leur convention pour la saisie.
const getConventionsWithAmendments = managementUnit => (managementUnit?.link_conventions ?? []).map(convention => ({
    ...convention,
    amendments: (managementUnit?.link_conventionAmendments ?? []).filter(a => a.conventionId === convention.conventionId),
}))

// Une convention supprimée ne doit plus être référencée par les compétences.
const getUnlinkedSkillConventions = (managementUnit, conventionId) => Object.fromEntries(SKILL_CONVENTION_KEYS
    .filter(key => managementUnit[key] === conventionId)
    .map(key => [key, undefined]))

const ManagementEntityDashboardApp = () => {
    const { code } = useParams()

    const {
        accountUser,
        managementUnitProps,
        contacts,
        cities,
        citiesPopulations,
        installations,
        distributionUnits,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        managementUnitProps: store.RpqsReducer.managementUnit,
        contacts: store.ContactReducer.contacts,
        cities: store.CityReducer.cities,
        citiesPopulations: store.RpqsReducer.citiesPopulations,
        installations: store.InstallationReducer.installationsLight,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(CityAction.fetchCities())
        dispatch(ReferencialAction.fetchSandreCodes())
        dispatch(ContactAction.fetchContacts())
        dispatch(InstallationThunk.fetchInstallationsLight())
        dispatch(DistributionUnitThunk.fetchDistributionUnits())
    }, [dispatch, code])

    const [isEditMode, setIsEditMode] = useState(false)
    const [managementUnit, setManagementUnit] = useState({})

    const [openDialogContacts, setOpenDialogContacts] = useState(false)
    const [selectedContacts, setSelectedContacts] = useState([])

    const [openDialogCities, setOpenDialogCities] = useState(false)
    const [selectedCities, setSelectedCities] = useState([])

    const [openDialogConventions, setOpenDialogConventions] = useState(false)
    const [selectedConventions, setSelectedConventions] = useState([])
    const [defaultValueConventions, setDefaultValueConventions] = useState({})
    const [isNewConventions, setIsNewConventions] = useState(true)

    const [openDialogInstallations, setOpenDialogInstallations] = useState(false)
    const [selectedInstallations, setSelectedInstallations] = useState([])

    const [openDialogDistributionUnits, setOpenDialogDistributionUnits] = useState(false)
    const [selectedDistributionUnits, setSelectedDistributionUnits] = useState([])

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

    const isLoaded = code === 'new' || managementUnitProps?.id === parseInt(code)

    useEffect(() => {
        if (code === 'new') {
            setManagementUnit({})
            setIsEditMode(true)
        } else if (isLoaded) {
            setManagementUnit(managementUnitProps)
        }
    }, [managementUnitProps, code, isLoaded])

    useTitle(() => {
        if (code !== 'new') {
            return [{
                title: i18n.rpqs,
                href: 'rpqs',
            }, {
                title: i18n.RPQSReferencials,
                href: 'rpqs/referencials',
            }, {
                title: i18n.managementEntities,
                href: PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY,
            }, {
                title: managementUnit?.name,
                href: `${PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY}/${managementUnit?.id}`,
            }]
        }
        return [{
            title: i18n.rpqs,
            href: 'rpqs',
        }, {
            title: i18n.RPQSReferencials,
            href: 'rpqs/referencials',
        }, {
            title: i18n.managementEntities,
            href: PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY,
        }, {
            title: i18n.new,
            href: PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY_NEW,
        }]
    }, [managementUnit, code])

    const disabled = useMemo(() => ({
        active: isEditMode,
        disabled: !isEditMode,
    }), [isEditMode])


    useEffect(() => {
        setSelectedContacts(managementUnit?.link_contacts?.map((contact) => contact.contactCode) ?? [])
    }, [managementUnit.link_contacts])

    const dataContacts = useMemo(() => {
        const contactsFiltered = contacts.filter((contact) => selectedContacts?.find(s => s === contact.id))
        return contactsFiltered.map((contact) => ({
            name: { value: contact.name },
            phoneTel: { value: contact.phoneTel },
            email: { value: contact.email },
            socialReason: { value: contact.socialReason },
        }))
    }, [selectedContacts, contacts])

    useEffect(() => {
        setSelectedCities(managementUnit?.link_cities?.map((city) => city.cityCode) ?? [])
    }, [managementUnit.link_cities])

    useEffect(() => {
        if (selectedCities.length) dispatch(CityAction.getCitiesPopulations(selectedCities))
    }, [selectedCities, dispatch])

    const citiesWithPopulation = useMemo(() => {
        const citiesFiltered = cities.filter((city) => selectedCities?.find(s => s === city.id))
        return citiesFiltered.map(city => {
            const population = citiesPopulations.find(p => p.cityCode === city.code)
            return {
                ...city,
                year: population?.year,
                inseeTotal: population?.inseeTotal,
                temporaryResident: population?.temporaryResident,
                increasedTotal: population?.increasedTotal,
            }
        })
    }, [selectedCities, citiesPopulations, cities])

    const dataCities = useMemo(() => citiesWithPopulation?.map((cP) => ({
        city: { value: cP.name },
        year: { value: cP.year },
        totalPopulationINSEE: { value: formatThousands(cP.inseeTotal) },
        temporaryResidents: { value: formatThousands(cP.temporaryResident) },
        adjustedTotalPopulation: { value: formatThousands(cP.increasedTotal) },
    })), [citiesWithPopulation])

    const inseeTotal = useMemo(() =>
        citiesWithPopulation.reduce((acc, city) =>
            acc + (city.inseeTotal ?? 0)
        , 0)
    , [citiesWithPopulation])

    const temporaryResident = useMemo(() =>
        citiesWithPopulation.reduce((acc, city) =>
            acc + (city.temporaryResident ?? 0)
        , 0)
    , [citiesWithPopulation])

    const increasedTotal = useMemo(() =>
        citiesWithPopulation.reduce((acc, city) =>
            acc + (city.increasedTotal ?? 0)
        , 0)
    , [citiesWithPopulation])

    useEffect(() => {
        setSelectedConventions(getConventionsWithAmendments(managementUnit))
    }, [managementUnit.link_conventions, managementUnit.link_conventionAmendments])

    const sandreConventionLinks = useSandreList(SANDRE.CONVENTIONS_LIEN)
    const skillsList = useSandreList(SANDRE.ENTITE_GESTION_COMPETENCE)

    const dataConventions = useMemo(() => selectedConventions.map((mU) => ({
        conventionId: mU.conventionId,
        agreement: { value: mU.convention },
        providerName: { value: mU.providerName },
        characteristics: { value: mU.characteristics },
        contractualRelationship: { value: sandreConventionLinks?.find(item => item.code === mU.contractualLink)?.name },
    })) ?? [], [selectedConventions, sandreConventionLinks])

    useEffect(() => {
        setSelectedInstallations(getLinkedStationCodes(managementUnit.link_stations, STATION_TYPE_CONSTANT.INSTALLATION))
        setSelectedDistributionUnits(getLinkedStationCodes(managementUnit.link_stations, STATION_TYPE_CONSTANT.DISTRIBUTION))
    }, [managementUnit.link_stations])

    const selectableInstallations = useMemo(() => getSelectableStations(installations), [installations])
    const selectableDistributionUnits = useMemo(() => getSelectableStations(distributionUnits), [distributionUnits])

    const dataInstallations = useMemo(() => getStationRows(selectedInstallations, installations, getInstallationIcon), [selectedInstallations, installations])
    const dataDistributionUnits = useMemo(() => getStationRows(selectedDistributionUnits, distributionUnits, getDistributionUnitIcon), [selectedDistributionUnits, distributionUnits])

    const onClickStation = stationType => row => !isNil(row.id) && dispatch(push(`/station/${stationType}/${row.id}`))

    const isConsultant = useMemo(() => accountUser.consultant === '1', [accountUser])

    const actionsContacts = [{
        icon: 'edit',
        color: 'white',
        displayed: isEditMode,
        onClick: () => {
            setOpenDialogContacts(true)
        },
        tooltip: i18n.addContributor,
    }]

    const actionsCities = [{
        icon: 'edit',
        color: 'white',
        displayed: isEditMode,
        onClick: () => {
            setOpenDialogCities(true)
        },
        tooltip: i18n.addCity,
    }]

    const actionsConventions = [{
        icon: 'add',
        color: 'white',
        displayed: isEditMode,
        onClick: () => {
            setIsNewConventions(true)
            setOpenDialogConventions(true)
        },
        tooltip: i18n.addAgreement,
    }]

    const actionsInstallations = [{
        icon: 'edit',
        color: 'white',
        displayed: isEditMode,
        onClick: () => setOpenDialogInstallations(true),
        tooltip: i18n.editSampleWorks,
    }]

    const actionsDistributionUnits = [{
        icon: 'edit',
        color: 'white',
        displayed: isEditMode,
        onClick: () => setOpenDialogDistributionUnits(true),
        tooltip: i18n.editDistributionUnits,
    }]

    const lineActionsConventions = [{
        icon: 'edit',
        onClick: (convention) => {
            setDefaultValueConventions(selectedConventions.find(selectedConvention => selectedConvention.conventionId === convention.conventionId))
            setIsNewConventions(false)
            setOpenDialogConventions(true)
        },
        displayed: isEditMode,
        align: ALIGN.RIGHT,
    }, {
        icon: 'delete',
        onClick: (convention) => {
            setSelectedConventions(selectedConventions.filter(selectedConvention => selectedConvention.conventionId !== convention.conventionId))
            setManagementUnit(prev => ({ ...prev, ...getUnlinkedSkillConventions(prev, convention.conventionId) }))
        },
        displayed: isEditMode,
        align: ALIGN.RIGHT,
    }]

    useActions(() => {
        if (isConsultant) {
            return {}
        }
        const isNewActions = code === 'new'
        const formattedManagementUnit = {
            ...managementUnit,
            id: -1,
        }
        const formattedLink = (managementUnitCode) => ({
            link_contacts: selectedContacts.map((contactId) => ({
                managementUnitCode,
                contactCode: contactId,
            })),
            link_cities: selectedCities.map((cityCode) => ({
                managementUnitCode,
                cityCode,
            })),
            link_conventions: selectedConventions.map(selectedConvention => ({ ...omit(selectedConvention, 'amendments'), managementUnitCode })),
            link_conventionAmendments: selectedConventions.flatMap(({ conventionId, amendments = [] }) => amendments.map(amendment => ({ ...amendment, conventionId, managementUnitCode }))),
            link_stations: [
                ...getStationLinks(selectedInstallations, STATION_TYPE_CONSTANT.INSTALLATION, managementUnitCode),
                ...getStationLinks(selectedDistributionUnits, STATION_TYPE_CONSTANT.DISTRIBUTION, managementUnitCode),
            ],
        })

        if (isNewActions) {
            return {
                save: () => {
                    dispatch(RpqsAction.createManagementUnit(formattedManagementUnit, formattedLink))
                    setIsEditMode(false)
                },
                cancel: () => {
                    dispatch(push(PATH_RPQS_REFERENCIAL_MANAGEMENT_ENTITY))
                    setIsEditMode(false)
                },
            }
        }
        if (isEditMode) {
            return {
                save: () => {
                    dispatch(RpqsAction.updateManagementUnit(
                        {
                            ...formattedManagementUnit,
                            ...formattedLink(parseInt(code)),
                            id: parseInt(code),
                        }, code))
                    setIsEditMode(false)
                },
                cancel: () => {
                    setManagementUnit(managementUnitProps)

                    setSelectedContacts(managementUnitProps?.link_contacts?.map((contact) => contact.contactCode) ?? [])
                    setSelectedCities(managementUnitProps?.link_cities?.map((city) => city.cityCode) ?? [])
                    setSelectedConventions(getConventionsWithAmendments(managementUnitProps))
                    setSelectedInstallations(getLinkedStationCodes(managementUnitProps?.link_stations, STATION_TYPE_CONSTANT.INSTALLATION))
                    setSelectedDistributionUnits(getLinkedStationCodes(managementUnitProps?.link_stations, STATION_TYPE_CONSTANT.DISTRIBUTION))

                    setIsEditMode(false)
                },
            }
        }
        return {
            edit: () => {
                setIsEditMode(true)
            },
            deleteCheck: () => {
                setOpenDeleteDialog(true)
            },
        }
    }, [isConsultant, managementUnit, code, isEditMode, selectedContacts, selectedCities, selectedConventions, selectedInstallations, selectedDistributionUnits])

    if (!isLoaded) {
        return <ProgressCard indeterminate round />
    }

    return (
        <>
            <Stack spacing={1}>
                <Card>
                    <CardTitle title={ i18n.description} icon={<DashboardIcon fontSize='large'/>} />
                    <CardContent>
                        <Grid container spacing={1}>
                            <Grid size={3}>
                                <NumberField
                                    title={ i18n.code }
                                    value={ managementUnit.code }
                                    maxLength={10}
                                    onChange={c => setManagementUnit({ ...managementUnit, code: c })}
                                    {...disabled}
                                />
                            </Grid>
                            <Grid size={9}>
                                <Input
                                    title={ i18n.name }
                                    value={ managementUnit.name }
                                    onChange={name => setManagementUnit({ ...managementUnit, name })}
                                    {...disabled}
                                />
                            </Grid>
                            <Grid size={4}>
                                <SimpleDatePicker
                                    label={ i18n.creationDate }
                                    value={ managementUnit.creationDate }
                                    onChange={creationDate => setManagementUnit({ ...managementUnit, creationDate })}
                                    { ...disabled }
                                />
                            </Grid>
                            <Grid size={4}>
                                <SimpleDatePicker
                                    label={ i18n.endDate }
                                    value={ managementUnit.endDate }
                                    onChange={endDate => setManagementUnit({ ...managementUnit, endDate })}
                                    { ...disabled }
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    title={ i18n.sispeaCode }
                                    value={ managementUnit.sispeaCode }
                                    onChange={sispeaCode => setManagementUnit({ ...managementUnit, sispeaCode })}
                                    maxLength={20}
                                    { ...disabled }
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    title={ i18n.codeUGE }
                                    value={ managementUnit.ugeCode }
                                    onChange={ugeCode => setManagementUnit({ ...managementUnit, ugeCode })}
                                    maxLength={20}
                                    { ...disabled }
                                />
                            </Grid>
                            <Grid offset={6} />
                            <Grid size={6}>
                                <SuperMultiAutocomplete
                                    label={i18n.folderFollowBy}
                                    options={contacts}
                                    values={ managementUnit.contactFollowUp }
                                    onChange={ contactFollowUp => {
                                        setManagementUnit({ ...managementUnit, contactFollowUp })
                                    } }
                                    {...disabled}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <Card>
                    <CardTitle title={i18n.contacts} icon={<ContactsIcon fontSize='large'/>}/>
                    <CardContent>
                        <Grid container spacing={1}>
                            <Grid size={4}>
                                <Input
                                    title={ i18n.title }
                                    value={ managementUnit.contactTitle }
                                    onChange={contactTitle => setManagementUnit({ ...managementUnit, contactTitle })}
                                    {...disabled}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    title={ i18n.phoneTel }
                                    value={ managementUnit.contactPhone }
                                    maxLength={14}
                                    onChange={contactPhone => setManagementUnit({ ...managementUnit, contactPhone })}
                                    {...disabled}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    title={ i18n.email }
                                    value={ managementUnit.email }
                                    onChange={email => setManagementUnit({ ...managementUnit, email })}
                                    {...disabled}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SimpleTextArea
                                    title={ i18n.openHours }
                                    value={ managementUnit.openHours }
                                    onChange={openHours => setManagementUnit(prev => ({ ...prev, openHours }))}
                                    rows={4}
                                    maxLength={255}
                                    disabled={ !isEditMode }
                                />
                            </Grid>
                            <Grid size={12}>
                                <CardTable
                                    title={i18n.contacts}
                                    rows={dataContacts}
                                    actions={actionsContacts}
                                    headers={HEADERS_CONTACTS}
                                    color={mainBlue}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                <ManagementUnitSkillsPanel
                    managementUnit={managementUnit}
                    onChange={changes => setManagementUnit(prev => ({ ...prev, ...changes }))}
                    conventions={selectedConventions}
                    skillsList={skillsList}
                    readMode={!isEditMode}
                />
                <Card>
                    <CardTitle title={i18n.members} icon={<HandshakeIcon fontSize='large'/>}/>
                    <CardContent>
                        <Grid container spacing={1}>
                            <Grid container size={12} alignItems='end'>
                                <Grid size={6}>
                                    <PopulationField
                                        title={ i18n.totalPopulationINSEE }
                                        value={ managementUnit.inseeTotalAuto ? inseeTotal : managementUnit.inseeTotal }
                                        onChange={insTotal => setManagementUnit({ ...managementUnit, inseeTotal: insTotal })}
                                        disabled={ !isEditMode || managementUnit.inseeTotalAuto}
                                    />
                                </Grid>
                                <Checkbox
                                    label={ i18n.calculAuto }
                                    checked={ managementUnit.inseeTotalAuto }
                                    onChange={inseeTotalAuto => setManagementUnit({ ...managementUnit, inseeTotalAuto })}
                                    {...disabled}
                                />
                            </Grid>
                            <Grid container size={12} alignItems='end'>
                                <Grid size={6}>
                                    <PopulationField
                                        title={ i18n.temporaryResidents }
                                        value={ managementUnit.temporaryResidentAuto ? temporaryResident : managementUnit.temporaryResident }
                                        onChange={tempResident => setManagementUnit({ ...managementUnit, temporaryResident: tempResident })}
                                        disabled={ !isEditMode || managementUnit.temporaryResidentAuto}
                                    />
                                </Grid>
                                <Checkbox
                                    label={ i18n.calculAuto }
                                    checked={ managementUnit.temporaryResidentAuto }
                                    onChange={temporaryResidentAuto => setManagementUnit({ ...managementUnit, temporaryResidentAuto })}
                                    { ...disabled }
                                />
                            </Grid>
                            <Grid container size={12} alignItems='end'>
                                <Grid size={6}>
                                    <PopulationField
                                        title={ i18n.adjustedTotalPopulation }
                                        value={ managementUnit.increasedTotalAuto ? increasedTotal : managementUnit.increasedTotal }
                                        onChange={incrTotal => setManagementUnit({ ...managementUnit, increasedTotal: incrTotal })}
                                        disabled={ !isEditMode || managementUnit.increasedTotalAuto}
                                    />
                                </Grid>
                                <Checkbox
                                    label={ i18n.calculAuto }
                                    checked={ managementUnit.increasedTotalAuto }
                                    onChange={increasedTotalAuto => setManagementUnit({ ...managementUnit, increasedTotalAuto })}
                                    { ...disabled }
                                />
                            </Grid>
                            <Grid size={12}>
                                <CardTable
                                    title={i18n.city}
                                    rows={dataCities}
                                    headers={HEADERS_POPULATION}
                                    color={mainBlue}
                                    actions={actionsCities}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <CardTable
                    title={i18n.agreements}
                    rows={dataConventions}
                    headers={HEADERS_CONVENTIONS}
                    color={mainBlue}
                    actions={actionsConventions}
                    lineActions={lineActionsConventions}
                />
                <CardTable
                    title={i18n.sampleWorks}
                    rows={dataInstallations}
                    headers={HEADERS_STATIONS}
                    color={mainBlue}
                    actions={actionsInstallations}
                    onClickRow={onClickStation(STATION_TYPE_NAME.installation)}
                />
                <CardTable
                    title={i18n.distributionUnitsName}
                    rows={dataDistributionUnits}
                    headers={HEADERS_STATIONS}
                    color={mainBlue}
                    actions={actionsDistributionUnits}
                    onClickRow={onClickStation(STATION_TYPE_NAME.distributionUnit)}
                />
            </Stack>
            <DialogStations
                open={openDialogInstallations}
                title={i18n.editSampleWorks}
                onClose={() => {
                    setSelectedInstallations(getLinkedStationCodes(managementUnit.link_stations, STATION_TYPE_CONSTANT.INSTALLATION))
                    setOpenDialogInstallations(false)
                }}
                onValidate={() => setOpenDialogInstallations(false)}

                stations={selectableInstallations}
                selectedStations={selectedInstallations}
                setSelectedStations={setSelectedInstallations}
            />
            <DialogStations
                open={openDialogDistributionUnits}
                title={i18n.editDistributionUnits}
                onClose={() => {
                    setSelectedDistributionUnits(getLinkedStationCodes(managementUnit.link_stations, STATION_TYPE_CONSTANT.DISTRIBUTION))
                    setOpenDialogDistributionUnits(false)
                }}
                onValidate={() => setOpenDialogDistributionUnits(false)}

                stations={selectableDistributionUnits}
                selectedStations={selectedDistributionUnits}
                setSelectedStations={setSelectedDistributionUnits}
            />

            <DialogContacts
                open={openDialogContacts}
                onClose={() => {
                    setSelectedContacts(managementUnit?.link_contacts?.map((contact) => contact.contactCode) ?? [])
                    setOpenDialogContacts(false)
                }}
                onValidate={() => setOpenDialogContacts(false)}

                contacts={contacts}
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
            />
            <DialogCities
                open={openDialogCities}
                onClose={() => {
                    setSelectedCities(managementUnit?.link_cities?.map((city) => city.cityCode) ?? [])
                    setOpenDialogCities(false)
                }}
                onValidate={() => setOpenDialogCities(false)}

                cities={cities}
                selectedCities={selectedCities}
                setSelectedCities={setSelectedCities}
            />
            <DialogConventions
                open={openDialogConventions}
                setOpen={setOpenDialogConventions}

                managementUnitCode={parseInt(code)}
                conventions={selectedConventions}
                setConventions={setSelectedConventions}

                defaultValue={defaultValueConventions}
                setDefaultValue={setDefaultValueConventions}
                isNew={isNewConventions}
                setIsNew={setIsNewConventions}
            />
            <Dialog open={openDeleteDialog}>
                <DefaultDialogTitle title={i18n.managementEntity} onClose={() => setOpenDeleteDialog(false)} />
                <DialogContent>
                    <Typography>
                        {i18n.sureDeleteManagementEntity}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>
                        {i18n.no}
                    </Button>
                    <Button
                        onClick={() => {
                            setOpenDeleteDialog(false)
                            dispatch(RpqsAction.deleteManagementUnit(parseInt(code)))
                        }}
                        variant='contained'
                        color='primary'
                    >
                        {i18n.yes}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ManagementEntityDashboardApp